import { useEffect, useState } from 'react';
import { useAppDispatch } from '../app/hooks';
import {
  InfoTransmissionMessage,
  messagingActions,
} from '../reducers/messagingSlice';
import useEventListener from './useEventListener.hooks';
import useKeyPress from './useKeyPress.hooks';
import useTTSMessage from './useTTSMessage.hooks';

/**
 * HITL-1530: Alt+click a labelled control in the CENTER PANE and the customer
 * hears that label.
 *
 * The Item Builder already has this idea in one place — the ⓘ button on a
 * modifier choice speaks its description (`MultipleSelectionModGroup`). This
 * generalises the trigger so it also works on the many controls that carry a
 * useful value but are far too small to hang an icon off. The agent stops
 * retyping into the omnibar what is already on their screen.
 *
 * Scope is deliberately literal: it speaks EXACTLY the text rendered as the
 * element's label. No menu lookup, no templates, no generated phrasing. So
 * values that are not painted on a control are out of reach — a combo's
 * DEFAULT fries size lives in the combo's option data and appears on no button,
 * and is not covered by this hook.
 *
 * Literal does not mean "every character inside the element", though. An
 * element that paints more than its label — the item header tab, name beside
 * the item's whole menu description; the mod group heading, name plus the
 * `(select 1 of 2)` counter — has to name the label part via `data-speak-text`.
 * For both, the plain click already sends the description, and the read-out is
 * for the name (HITL-1532).
 *
 * Scope is the CENTER PANE ONLY — the `data-alt-readout-scope` subtree, which
 * is ItemBuilder in both the HITL 3.0 and legacy layouts. That pane is where
 * the menu is: sizes, options, modifier choices, the values a customer's
 * question is actually about. Controls in the other panes ACT rather than
 * inform — the cart's quantity and delete buttons, the action bar, the header —
 * and reading their labels out would be noise at best. Outside the scope an
 * Alt+click is ignored entirely and the control behaves normally.
 *
 * Why one scoped listener instead of an `onClick` on each component: the
 * eligible controls inside that pane are many and varied (chips, buttons, mod
 * choices) and the trigger has to be uniform. One listener also gives the
 * property the per-component version cannot — see "swallowing" below.
 *
 * Capture on `window` is the same shape the keyboard shortcuts use
 * (`useIntentHotkeys`, `useFlagIssueHotkey`): window capture runs strictly
 * before React's own listeners on the root container, so `stopPropagation()`
 * here means the element's `onClick` never runs at all.
 *
 * That swallowing is the point, and it applies to EVERY eligible element —
 * including one with no readable label. An agent holding Alt has asked to
 * hear something, not to press the button; if an Alt+click on an icon-only
 * IconButton fell through to its handler it would delete a cart line or fire an
 * intent to the customer. So an unlabelled control is a true no-op: nothing is
 * spoken and nothing is pressed.
 *
 * Speaking is taking the turn. `sendTTSMessage` stamps a `TTS_SENT` agent
 * interception, and the ai slice ends AI auto mode on that action — so an
 * Alt+click while the AI is driving hands the conversation to the agent,
 * exactly as typing a message would. Expected, not a defect.
 */

// Controls that carry a label worth speaking. `.MuiChip-root` is listed
// explicitly because a non-clickable MUI Chip — the mod-choice chips are the
// main case — renders a plain div with no role. `[data-speakable]` is the
// opt-in for center-pane text that is not a control at all, so a label can join
// without being made into a button.
export const SPEAKABLE_SELECTOR =
  'button, [role="button"], .MuiChip-root, [data-speakable]';

// Only controls inside this subtree read out. Set once, on ItemBuilder.
export const READOUT_SCOPE_SELECTOR = '[data-alt-readout-scope]';

// Escape hatch for an element whose visible text is not what should be said.
// Two cases, both HITL-1532: an element that paints a value AND prose about it
// — the item header tab, name beside the item's whole menu description — and
// one that paints a value plus decorations meant for the agent — the mod group
// heading, whose `(select 1 of 2)` counter and `!` unmet marker are no part of
// the question. Setting this attribute names the part worth speaking.
export const SPEAK_TEXT_ATTRIBUTE = 'data-speak-text';

// Set on <body> while Alt is held so eligible controls can advertise
// themselves. Styled in index.css — it has to reach arbitrary elements the
// theme does not own, which is the one thing a global stylesheet is for.
export const ALT_READOUT_BODY_CLASS = 'hitl-alt-readout';

// Past this, whatever was matched is a container rather than a label, and
// reading it would put a paragraph in the customer's ear. Saying nothing is the
// better failure.
//
// It guards the INFERRED label only. `data-speak-text` is authored in code and
// says precisely what to speak, so it is never an accidental container — and
// capping it would make the read-out silently depend on the length of text the
// override exists to exclude (HITL-1532: a long item description blanked the
// header read-out even once the name was named explicitly).
export const MAX_LABEL_LENGTH = 120;

/**
 * The text a control should speak: its explicit override, else its rendered
 * label with whitespace collapsed. Empty when there is nothing sensible to say
 * — an icon-only button, or an inferred match so large it cannot be a label.
 */
export const getSpeakableLabel = (element: Element): string => {
  // Underscores are how the menu writes a name that was never meant to be
  // spoken — brand-menu constraint names come through as `shake_flavor` — and
  // TTS says them out loud. They are word separators, so speak them as spaces
  // (HITL-1532).
  //
  // Here and nowhere else: the agent goes on seeing the real name on screen,
  // which is what they match against the menu and against each other. This
  // rewrites what is said, not what is shown.
  const collapse = (raw: string) =>
    raw.replace(/_+/g, ' ').replace(/\s+/g, ' ').trim();

  const override = element.getAttribute(SPEAK_TEXT_ATTRIBUTE);
  if (override !== null) {
    return collapse(override);
  }

  const label = collapse(element.textContent ?? '');
  return label.length > MAX_LABEL_LENGTH ? '' : label;
};

const useAltClickReadout = () => {
  const dispatch = useAppDispatch();
  const { sendTTSMessage } = useTTSMessage();

  // Plain `useKeyPress('Alt')`, NOT the `{ isAltKey: true }` form: on the Alt
  // keyup `event.altKey` is already false, so that form never sees the release
  // and would latch the affordance on forever.
  const isAltDown = useKeyPress('Alt');
  // useKeyPress also misses the keyup when the release happens in another
  // window (Alt+Tab). Latch the affordance off on blur, and re-arm on the next
  // real Alt press. Cosmetic only — the handler below reads `event.altKey`,
  // which is never stale.
  const [isBlurredWhileDown, setIsBlurredWhileDown] = useState(false);
  useEventListener('blur', () => setIsBlurredWhileDown(true), window);
  useEventListener('keydown', (event) => {
    if ((event as KeyboardEvent).key === 'Alt') setIsBlurredWhileDown(false);
  });

  const isAltHeld = isAltDown && !isBlurredWhileDown;

  useEffect(() => {
    document.body.classList.toggle(ALT_READOUT_BODY_CLASS, isAltHeld);
    return () => {
      document.body.classList.remove(ALT_READOUT_BODY_CLASS);
    };
  }, [isAltHeld]);

  const handleClick = (event: Event) => {
    const e = event as MouseEvent;
    // Alt alone. Alt+Shift+click and friends stay available to whatever else
    // may want them, and never speak by accident.
    if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) {
      return;
    }
    const target = e.target as Element | null;
    // Center pane only. Checked before the control match so an Alt+click
    // anywhere else falls through untouched, rather than being swallowed.
    const scope = target?.closest?.(READOUT_SCOPE_SELECTOR);
    if (!target || !scope) {
      return;
    }
    const element = target.closest(SPEAKABLE_SELECTOR);
    // `closest` walks past the scope root, so a control wrapping the whole pane
    // would match from a target inside it. Nothing in the tree does that today;
    // the check is here so "outside the pane is untouched" is a property of the
    // code rather than of the current markup.
    if (!element || !scope.contains(element)) {
      return;
    }

    // Swallow first, decide second — see the docblock. An eligible control is
    // never pressed by an Alt+click, whether or not it turns out to be
    // readable.
    e.preventDefault();
    e.stopPropagation();

    const label = getSpeakableLabel(element);
    if (!label) {
      return;
    }

    sendTTSMessage(label);

    // Stable code + structured metadata so read-out usage is aggregable in New
    // Relic (same shape as ORDER_RETRY_CLICKED).
    const payload: Partial<InfoTransmissionMessage> = {
      data: {
        message: `Alt+click read-out: "${label}"`,
        type: 'METRIC',
        code: 'ALT_CLICK_READOUT',
        metadata: { label, element: element.tagName.toLowerCase() },
      },
    };
    dispatch(messagingActions.sendInfo(payload as any));
  };

  useEventListener('click', handleClick, window, true);
};

export default useAltClickReadout;
